import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Button,
  Avatar,
  Card,
  CardContent,
  Chip
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { apiClient } from '../../lib/apiClient';

function ViewChurch({ user }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [church, setChurch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchChurch = async () => {
      try {
        const data = await apiClient.getChurch(id);
        setChurch(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (user && id) {
      fetchChurch();
    }
  }, [user, id]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error">{error}</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/super-admin/churches')}
          sx={{ mt: 2 }}
        >
          Back to Churches
        </Button>
      </Box>
    );
  }

  if (!church) {
    return (
      <Box>
        <Alert severity="info">Church not found</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/super-admin/churches')}
          sx={{ mt: 2 }}
        >
          Back to Churches
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/super-admin/churches')}
        >
          Back
        </Button>
        <Typography variant="h4" sx={{ fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
          Church Details
        </Typography>
      </Box>

      <Paper sx={{ p: { xs: 2, sm: 3 }, mt: 3, maxWidth: { xs: '100%', md: 800 } }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Logo */}
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Avatar
              src={church.logoUrl}
              sx={{ width: { xs: 100, sm: 150 }, height: { xs: 100, sm: 150 } }}
            >
              {church.name?.charAt(0)}
            </Avatar>
          </Box>

          {/* Name and Status */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h5" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
              {church.name}
            </Typography>
            <Chip
              label={church.isActive ? 'Active' : 'Inactive'}
              color={church.isActive ? 'success' : 'default'}
              size="small"
            />
          </Box>

          {/* Address */}
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Address
              </Typography>
              <Typography variant="body1">
                {church.address?.address || '-'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {church.address?.city || ''}
                {church.address?.city && church.address?.state ? ', ' : ''}
                {church.address?.state || ''}
                {church.address?.pincode ? ` ${church.address.pincode}` : ''}
              </Typography>
              {church.address?.country && (
                <Typography variant="body2" color="text.secondary">
                  {church.address.country}
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* Church Admins */}
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Church Admins
              </Typography>
              <Typography variant="h6">
                {church.churchAdminIds?.length || 0}
              </Typography>
            </CardContent>
          </Card>

          {/* Description */}
          {church.description && (
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Description
                </Typography>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {church.description}
                </Typography>
              </CardContent>
            </Card>
          )}
        </Box>
      </Paper>
    </Box>
  );
}

export default ViewChurch;

